import React from "react";
import { motion } from "framer-motion";
import { Handshake, ShieldCheck, Clock, MessageSquare } from "lucide-react";
import PageMeta from "../site/PageMeta";
import Chapter from "../site/Chapter";
import { Link } from "react-router-dom";

const values = [
  {
    icon: Handshake,
    title: "Ownership",
    body: "Every engagement has a named person accountable for it — not a ticket queue.",
  },
  {
    icon: ShieldCheck,
    title: "Security",
    body: "NDAs, defined work-ownership terms, and controlled data access on every engagement, by default.",
  },
  {
    icon: Clock,
    title: "Availability",
    body: "Support structured around your hours, not just ours.",
  },
  {
    icon: MessageSquare,
    title: "Straight talk",
    body: "If something isn't working, you'll hear it from us before you have to ask.",
  },
];

// Team roster
const team = [
  {
    name: "Shreya Batra",
    title: "Founder",
    photo: `${process.env.PUBLIC_URL}/assets/shreya.jpg`,
    initials: "SB",
  },
  {
    name: "Sahil Batra",
    title: "CEO",
    photo: `${process.env.PUBLIC_URL}/assets/sahil.jpeg`,
    initials: "SB",
  },
];

export default function About() {
  return (
    <>
      <PageMeta
        title="About A4 Assist — Why We Exist"
        description="A4 Assist is an AI-enabled operations partner for growing businesses. Founded by Sahil Batra, backed by a decade in recruitment and outsourcing operations."
      />

      <section className="pt-36 pb-14">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <Chapter
            number="00"
            eyebrow="About A4 Assist"
            title={
              <>
                Why A4 Assist <span className="font-serif italic">exists</span>.
              </>
            }
          />
        </div>
      </section>

      {/* Our Story */}
      <section className="pb-20">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-4 text-base sm:text-lg text-slate-400 leading-relaxed">
            <p>
              A4 Assist was built on a simple observation: most growing
              businesses don't need more headcount, they need more capacity —
              the right people doing the right work, without the time and cost
              of building an entire function from scratch.
            </p>
            <p>
              We started in recruitment, helping companies fill roles faster
              and more precisely. As clients asked us to take on more of their
              operational and data work, we built that capability too, with
              AI-assisted tools from the ground up so our teams move faster
              without cutting corners.
            </p>
            <p className="text-slate-300">
              Today, A4 Assist supports clients across recruiting, business
              operations, and knowledge work, with the same standard we
              started with:{" "}
              <span className="font-serif italic text-white">real people, doing real work, accountable for real results.</span>
            </p>
          </div>
          <div className="lg:col-span-4 space-y-6">
            <div className="rounded-2xl border border-slate-800 bg-[#0A1325]/60 p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#00E5FF]">
                Mission
              </div>
              <p className="mt-3 text-slate-300 leading-relaxed">
                To be the operating partner growing businesses actually need:
                fast to bring in, easy to work with, and honest about what we
                can and can't do well.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-[#0A1325]/60 p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#A3FF5E]">
                Vision
              </div>
              <p className="mt-3 text-slate-300 leading-relaxed">
                A world where scaling a business doesn't mean choosing between
                doing everything yourself or handing it off to a faceless vendor.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="pb-24 border-y border-slate-900 bg-[#070F1F] py-20 relative">
        <div className="absolute inset-0 bg-radial-cyan opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10">
          <Chapter
            number="01"
            eyebrow="Values"
            title={
              <>
                How we{" "}
                <span className="font-serif italic">actually</span> operate.
              </>
            }
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="rounded-xl border border-slate-800 bg-[#0A1325]/60 p-6"
                data-testid={`value-card-${i}`}
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20">
                  <v.icon size={18} />
                </div>
                <h3 className="font-display mt-4 text-base font-semibold text-white">
                  {v.title}
                </h3>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">
                  {v.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <Chapter
            number="02"
            eyebrow="Our Team"
            accent="lime"
            title={
              <>
                Led by the{" "}
                <span className="font-serif italic">Batra</span> family.
              </>
            }
            subtitle="A4 Assist is founded by Shreya Batra and led as CEO by Sahil Batra, who has spent over a decade in recruitment and outsourcing — including senior leadership roles running delivery and operations for staffing and outsourcing firms — before founding A4 Assist to bring that experience to a broader range of business functions."
          />

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl">
            {team.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`team-card-${i}`}
                className="group rounded-2xl border border-slate-800 bg-[#0A1325]/60 overflow-hidden hover:border-[#00E5FF]/40 transition-colors"
              >
                <div className="portrait-spotlight aspect-[4/5] bg-[#0F1A30]">
                  <img
                    src={m.photo}
                    alt={`${m.name} — ${m.title}`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#00E5FF]">
                    {m.title}
                  </div>
                  <div className="font-display mt-2 text-xl font-semibold text-white">
                    {m.name}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl border border-slate-800 bg-[#0A1325]/60 p-8">
            <div>
              <h3 className="font-display text-xl sm:text-2xl font-semibold text-white">
                Want to work with A4 Assist?
              </h3>
              <p className="mt-1.5 text-sm text-slate-400 max-w-lg">
                We're always open to meeting good people, and always open to
                hearing from businesses who need capacity.
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/careers"
                className="inline-flex items-center gap-2 border border-slate-700 hover:border-[#00E5FF]/60 text-white font-semibold px-6 py-3.5 rounded-md"
              >
                Careers
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#050B14] font-semibold px-6 py-3.5 rounded-md shadow-[0_0_30px_-8px_rgba(0,229,255,0.6)]"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
